/**
 * Sound manifest — transcription of 08 §4a–4e (+ 15 §4.1 production list).
 * The manifest is data: `vars` is the number of variations (round-robin, no
 * repeat); bumping it is the only change needed to add a variation (A08-15).
 * Every id has a `synth` recipe so the P0 code-synth path stays alive
 * forever (15 §7 `synthFallback`). Reserved ids render silence and are never
 * triggered.
 */
export type Bus = 'sfx' | 'ui' | 'voice' | 'music'
export type Priority = 0 | 1 | 2 | 3

export type SynthName =
  | 'thwip' | 'whir_loop' | 'whir_trem_loop' | 'slap' | 'ring_steel' | 'tick_tight' | 'thud_pad' | 'ping_pen'
  | 'clank_double' | 'thump_body' | 'clack' | 'snap_glove' | 'drop_rattle' | 'scoop' | 'two_note_down'
  | 'pen_fwd_short' | 'pen_rev_short' | 'paper_tick' | 'nib_click' | 'step' | 'cloth' | 'scuff' | 'plant'
  | 'plate_snap' | 'servo_loop' | 'plate_retract' | 'clatter_exhale' | 'inhale' | 'double_tick' | 'pen_rev_full'
  | 'lpf_drop' | 'pen_fwd_full' | 'tick_rub' | 'rail_scuff' | 'click_pair' | 'paper_slide' | 'tick' | 'tick_rev'
  | 'click_low' | 'metronome' | 'stamp' | 'etch_pair' | 'count_tick' | 'count_go' | 'timer_tick' | 'sting_two'
  | 'tag_rise' | 'tag_fall' | 'sting_low' | 'pen_rev_slow_loop' | 'flat_tick' | 'sting_void' | 'coin_tick'
  | 'coin_land' | 'score_click' | 'sting_long' | 'etch' | 'hold_in' | 'hold_loop' | 'hold_out' | 'room_tone'
  | 'riser' | 'bandpass_sting' | 'bracket_open' | 'bracket_close' | 'slider_tick' | 'bind_ok' | 'bind_warn'
  | 'error_tick' | 'unlock' | 'hum' | 'hall' | 'jump_scuff' | 'land_plant' | 'stem_tick' | 'stem_pulse' | 'stem_kit' | 'stem_metal' | 'stem_low'
  | 'stem_ride' | 'stem_drone' | 'stem_hub' | 'stem_results' | 'silence'

export interface SoundDef {
  pri: Priority
  bus: Bus
  spatial: boolean
  vars: number
  poly: number
  cooldownMs: number
  loop: boolean
  /** Length cap in seconds (08 §8; loops: exact length). */
  maxS: number
  synth: SynthName
  gainDb?: number
  /** Module-relative URL when the id ships as a file (see `files.ts`); absent → live synth only. */
  src?: string
  reserved?: boolean
}

const def = (pri: Priority, bus: Bus, spatial: boolean, vars: number, poly: number, cooldownMs: number, synth: SynthName, maxS = 1.0, extra: Partial<SoundDef> = {}): SoundDef =>
  ({ pri, bus, spatial, vars, poly, cooldownMs, loop: false, maxS, synth, ...extra })
const loop = (pri: Priority, bus: Bus, spatial: boolean, poly: number, synth: SynthName, lenS: number, extra: Partial<SoundDef> = {}): SoundDef =>
  ({ pri, bus, spatial, vars: 1, poly, cooldownMs: 0, loop: true, maxS: lenS, synth, ...extra })

export const SOUNDS = {
  // 4a disc
  disc_throw: def(2, 'sfx', true, 4, 4, 40, 'thwip', 0.6),
  disc_flight_loop: loop(1, 'sfx', true, 12, 'whir_loop', 2, { gainDb: -4.0 }),
  disc_bounce_wall: def(1, 'sfx', true, 5, 4, 30, 'slap', 0.6),
  disc_bounce_pillar: def(1, 'sfx', true, 4, 3, 30, 'ring_steel', 0.6, { gainDb: -1.9 }),
  disc_bounce_corner_tick: def(1, 'sfx', true, 2, 2, 30, 'tick_tight', 0.3),
  shield_block_late: def(2, 'sfx', true, 3, 3, 40, 'thud_pad', 0.8),
  shield_deflect_perfect: def(3, 'sfx', true, 3, 2, 0, 'ping_pen', 0.8, { gainDb: -5.3 }),
  disc_clash: def(2, 'sfx', true, 3, 2, 60, 'clank_double', 0.6),
  disc_hit_body: def(3, 'sfx', true, 3, 2, 0, 'thump_body', 0.6, { gainDb: -3.1 }),
  hit_bank_tag: def(2, 'ui', false, 1, 1, 200, 'clack', 0.3),
  disc_return_loop: loop(0, 'sfx', true, 8, 'whir_trem_loop', 2, { gainDb: -1.1 }),
  disc_catch: def(2, 'sfx', true, 4, 3, 40, 'snap_glove', 0.6, { gainDb: -0.3 }),
  disc_ground_drop: def(1, 'sfx', true, 3, 3, 50, 'drop_rattle', 0.6),
  disc_pickup: def(1, 'sfx', true, 3, 2, 60, 'scoop', 0.6),
  steal_tag: def(2, 'ui', false, 1, 1, 300, 'two_note_down', 0.4, { gainDb: -1.3 }),
  pen_redraw_short: def(1, 'sfx', true, 2, 2, 100, 'pen_fwd_short', 0.3),
  pen_undraw_short: def(0, 'sfx', true, 2, 2, 100, 'pen_rev_short', 0.25, { gainDb: -1.2 }),
  return_lost_tick: def(1, 'sfx', true, 1, 2, 100, 'paper_tick', 0.3, { gainDb: -0.3 }),
  disc_arm: def(2, 'sfx', true, 1, 4, 0, 'nib_click', 0.3),
  // 4b player
  step_concrete: def(0, 'sfx', true, 6, 8, 120, 'step', 0.3, { gainDb: -6 }),
  throw_windup: def(1, 'sfx', true, 2, 4, 40, 'cloth', 0.3),
  dash: def(2, 'sfx', true, 4, 4, 100, 'scuff', 0.5, { gainDb: -0.3 }),
  dash_land: def(0, 'sfx', true, 3, 4, 100, 'plant', 0.4),
  // S10 jump (spec §5): take-off cloth + heel scuff; landing plant + short concrete slap.
  jump: def(1, 'sfx', true, 3, 4, 80, 'jump_scuff', 0.4),
  jump_land: def(1, 'sfx', true, 3, 4, 80, 'land_plant', 0.5),
  shield_raise: def(2, 'sfx', true, 3, 4, 80, 'plate_snap', 0.5),
  shield_hold_loop: loop(1, 'sfx', true, 4, 'servo_loop', 2, { gainDb: -5.7 }),
  shield_lower: def(1, 'sfx', true, 3, 4, 80, 'plate_retract', 0.5),
  exhaust: def(2, 'sfx', true, 2, 2, 200, 'clatter_exhale', 0.8, { gainDb: -1.9 }),
  exhaust_recover: def(0, 'sfx', true, 2, 2, 200, 'inhale', 0.5, { gainDb: -3.7 }),
  stamina_refuse: def(1, 'ui', false, 1, 1, 150, 'double_tick', 0.3),
  derez_undraw: def(3, 'sfx', true, 2, 2, 0, 'pen_rev_full', 1.5, { gainDb: -0.7 }),
  derez_local_tail: def(3, 'ui', false, 1, 1, 0, 'lpf_drop', 0.6),
  pen_redraw_full: def(2, 'sfx', true, 2, 2, 0, 'pen_fwd_full', 1.0, { gainDb: -0.9 }),
  invuln_end: def(1, 'sfx', true, 1, 2, 0, 'tick_rub', 0.5),
  bounds_rail: def(0, 'sfx', true, 2, 2, 250, 'rail_scuff', 0.4),
  // 4c round / match flow
  ui_pick_open: def(2, 'ui', false, 1, 1, 0, 'click_pair', 0.4, { gainDb: -0.3 }),
  ui_card_hover: def(0, 'ui', false, 2, 2, 40, 'paper_slide', 0.3, { gainDb: -0.9 }),
  ui_card_select: def(1, 'ui', false, 2, 2, 40, 'tick', 0.3),
  ui_card_deselect: def(1, 'ui', false, 2, 2, 40, 'tick_rev', 0.3),
  ui_lock_open: def(2, 'ui', false, 1, 1, 0, 'click_low', 0.4),
  ui_lock_tick: def(1, 'ui', false, 2, 1, 0, 'metronome', 0.3),
  ui_lock_confirm: def(2, 'ui', false, 1, 1, 0, 'stamp', 0.4),
  ui_reveal: def(2, 'ui', false, 1, 1, 0, 'etch_pair', 0.5, { gainDb: -4.0 }),
  count_tick: def(3, 'voice', false, 3, 1, 0, 'count_tick', 0.4),
  count_go: def(3, 'voice', false, 1, 1, 0, 'count_go', 0.6, { gainDb: -1.1 }),
  timer_last10_tick: def(2, 'ui', false, 2, 1, 0, 'timer_tick', 0.3, { gainDb: -0.4 }),
  round_end_sting: def(3, 'voice', false, 1, 1, 0, 'sting_two', 1.5),
  round_win_tag: def(2, 'voice', false, 1, 1, 0, 'tag_rise', 0.5),
  round_lose_tag: def(2, 'voice', false, 1, 1, 0, 'tag_fall', 0.5),
  overtime_sting: def(3, 'voice', false, 1, 1, 0, 'sting_low', 1.5, { gainDb: -3.0 }),
  overtime_floor_undraw: loop(1, 'sfx', true, 1, 'pen_rev_slow_loop', 4, { gainDb: -1.6 }),
  overtime_step_tick: def(1, 'sfx', true, 1, 1, 900, 'nib_click', 0.3),
  ladder_tag: def(2, 'voice', false, 1, 1, 0, 'flat_tick', 0.4),
  void_round_sting: def(3, 'voice', false, 1, 1, 0, 'sting_void', 1.5),
  coin_tick: def(3, 'voice', false, 1, 1, 0, 'coin_tick', 0.3),
  coin_land: def(3, 'voice', false, 1, 1, 0, 'coin_land', 0.6),
  ui_score_bump: def(2, 'ui', false, 1, 2, 100, 'score_click', 0.3),
  match_end_sting: def(3, 'voice', false, 1, 1, 0, 'sting_long', 1.5),
  match_win_tag: def(2, 'voice', false, 1, 1, 0, 'tag_rise', 0.6),
  match_lose_tag: def(2, 'voice', false, 1, 1, 0, 'tag_fall', 0.6, { gainDb: -2.2 }),
  ui_row_in: def(0, 'ui', false, 2, 4, 60, 'paper_slide', 0.3, { gainDb: -0.7 }),
  ui_accolade: def(1, 'ui', false, 1, 2, 150, 'etch', 0.4, { gainDb: -2.5 }),
  pause_in: def(2, 'ui', false, 1, 1, 0, 'hold_in', 0.4, { gainDb: -3.6 }),
  pause_loop: loop(2, 'ui', false, 1, 'hold_loop', 2, { gainDb: -18 }),
  pause_out: def(2, 'ui', false, 1, 1, 0, 'hold_out', 0.4, { gainDb: -3.5 }),
  // 4d hub
  hub_enter: def(2, 'sfx', false, 1, 1, 0, 'room_tone', 0.6, { gainDb: -2.8 }),
  ui_queue_on: def(2, 'ui', false, 1, 1, 100, 'bracket_open', 0.3),
  ui_queue_off: def(2, 'ui', false, 1, 1, 100, 'bracket_close', 0.3),
  ui_queue_count: def(0, 'ui', false, 1, 1, 500, 'tick', 0.3),
  match_pull: def(3, 'voice', false, 1, 1, 0, 'riser', 0.8, { gainDb: -1.7 }),
  ui_side_switch: def(1, 'ui', false, 1, 1, 150, 'tick', 0.3),
  kiosk_tick: def(0, 'sfx', true, 1, 1, 2000, 'bandpass_sting', 0.6),
  hub_join: def(0, 'sfx', true, 1, 2, 300, 'tick', 0.3, { gainDb: -0.8 }),
  hub_leave: def(0, 'sfx', true, 1, 2, 300, 'tick_rev', 0.3),
  // 4e UI / options
  ui_open: def(1, 'ui', false, 1, 1, 0, 'bracket_open', 0.3, { gainDb: -0.5 }),
  ui_close: def(1, 'ui', false, 1, 1, 0, 'bracket_close', 0.3),
  ui_move: def(0, 'ui', false, 1, 1, 40, 'tick', 0.2, { gainDb: -0.6 }),
  ui_confirm: def(1, 'ui', false, 1, 1, 0, 'click_pair', 0.3),
  ui_cancel: def(1, 'ui', false, 1, 1, 0, 'tick_rev', 0.3),
  ui_slider: def(0, 'ui', false, 1, 1, 30, 'slider_tick', 0.15, { gainDb: -1.0 }),
  ui_bind_ok: def(1, 'ui', false, 1, 1, 0, 'bind_ok', 0.3),
  ui_bind_warn: def(1, 'ui', false, 1, 1, 0, 'bind_warn', 0.4),
  ui_tutorial_step: def(2, 'ui', false, 1, 1, 0, 'etch', 0.4, { gainDb: -2.4 }),
  ui_error: def(2, 'ui', false, 1, 1, 0, 'error_tick', 0.3),
  unlock_tick: def(1, 'ui', false, 1, 1, 0, 'unlock', 0.2, { gainDb: -0.3 }),
  // ambience (music bus, looped)
  amb_hub_room: loop(1, 'music', false, 1, 'room_tone', 12, { gainDb: -13.1 }),
  amb_kiosk_hum: loop(0, 'music', true, 1, 'hum', 12, { gainDb: -20.8 }),
  amb_arena: loop(1, 'music', false, 1, 'hall', 12, { gainDb: -14 }),
  // stems (08 §6, 15 §4.2) — composed loops; gainDb mirrors assets/audio/stems/gains.json (file and fallback at one level)
  stem_tick: loop(1, 'music', false, 1, 'stem_tick', 20, { gainDb: -2.1 }),
  stem_pulse_low: loop(1, 'music', false, 1, 'stem_pulse', 20, { gainDb: -5.2 }),
  stem_kit: loop(1, 'music', false, 1, 'stem_kit', 20, { gainDb: -2.4 }),
  stem_metal: loop(1, 'music', false, 1, 'stem_metal', 20, { gainDb: -2.1 }),
  stem_low_hits: loop(1, 'music', false, 1, 'stem_low', 20, { gainDb: -4.2 }),
  stem_ride: loop(1, 'music', false, 1, 'stem_ride', 20, { gainDb: -3.1 }),
  stem_drone: { pri: 1, bus: 'music', spatial: false, vars: 1, poly: 1, cooldownMs: 0, loop: false, maxS: 45, synth: 'stem_drone', gainDb: -3.1 } as SoundDef,
  stem_hub: loop(1, 'music', false, 1, 'stem_hub', 20, { gainDb: -2 }),
  stem_results: loop(1, 'music', false, 1, 'stem_results', 30, { gainDb: -1.8 }),
  // reserved
  fall: def(0, 'sfx', true, 1, 1, 0, 'silence', 0.1, { reserved: true }),
  vo_round: def(3, 'voice', false, 1, 1, 0, 'silence', 0.1, { reserved: true }),
  vo_overtime: def(3, 'voice', false, 1, 1, 0, 'silence', 0.1, { reserved: true }),
  vo_double_derez: def(3, 'voice', false, 1, 1, 0, 'silence', 0.1, { reserved: true }),
} satisfies Record<string, SoundDef>

export type SoundId = keyof typeof SOUNDS
export const SOUND_IDS = Object.keys(SOUNDS) as SoundId[]
export const isStem = (id: SoundId): boolean => id.startsWith('stem_')
export const isAmbience = (id: SoundId): boolean => id.startsWith('amb_')
export const isBounce = (id: SoundId): boolean => id.startsWith('disc_bounce_')
export const soundDef = (id: SoundId): SoundDef => SOUNDS[id]
